import cn from 'classnames';
import { type FC } from 'react';

import { GroupChipList, type GroupChipListItem } from '@/components/UI/GroupChip/GroupChip';
import { _isEmpty, _notNil } from '@/littledash';
import type { GroupApiId } from '@/model/TreatmentGroup.model';

import { useOverviewAccordion } from '../OverviewAccordionContext';
import styles from './OverviewGroupChips.module.scss';

interface OverviewGroupChipsProps {
  groupApiIds: Array<GroupApiId> | undefined;
  revealGroupsOnClick?: boolean;
}

/**
 * Resolves group api_ids against the Groups section and renders them as group chips.
 * Ids the Groups section did not return are skipped. With `revealGroupsOnClick`, the
 * chips open and scroll to the Groups section.
 */
const OverviewGroupChips: FC<OverviewGroupChipsProps> = ({ groupApiIds, revealGroupsOnClick = false }) => {
  const { groupsByApiId, revealSection } = useOverviewAccordion();
  const chips = (groupApiIds ?? []).reduce<Array<GroupChipListItem>>((acc, apiId) => {
    const group = groupsByApiId.get(apiId);
    if (_notNil(group)) {
      acc.push({ key: apiId, no: group.no, color: group.color, testId: `overview-group-chip-${apiId}` });
    }
    return acc;
  }, []);

  if (_isEmpty(chips)) {
    return <>-</>;
  }

  if (!revealGroupsOnClick) {
    return <GroupChipList chips={chips} />;
  }

  return (
    <button
      type="button"
      className={cn('link-button', styles.fill)}
      aria-label="Show in Groups section"
      data-test-component="OverviewGroupChips"
      data-test-element="reveal-groups"
      onClick={() => revealSection('groups')}
    >
      <GroupChipList chips={chips} />
    </button>
  );
};

export default OverviewGroupChips;
